import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import numeral from 'numeral';
import { ValidationIndicator } from '../ValidationIndicator';
import { useHyperFormulaPPESG } from '../../hooks/singapore/useHyperFormulaPPESG';
import { handleXBRLFieldBlur } from '../../utils/xbrlMandatoryFields';
import { applyHideEmptyColumns, toggleColumnVisibilityForSection } from '../../utils/columnVisibility';

interface PPETableSGProps {
  section: any;
  companyInfo: {
    consolidated: string;
    first_year: string;
    // Extended companyInfo with period dates
    reporting_period?: {
      opening: string;
      start: string;
      end: string;
    };
    previous_period?: {
      opening: string;
      start: string;
      end: string;
    };
    // Fallback for different structure
    currentPeriod?: {
      opening: string;
      start: string;
      end: string;
    };
    previousPeriod?: {
      opening: string;
      start: string;
      end: string;
    };
  };
  negativeTargets?: string[];
  globalHyperFormula?: {
    isReady: boolean;
    readOnlyFields: Set<string>;
    handleFieldChange: (fieldName: string, contextRef: string, value: string) => void;
    getCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  };
  onFieldChange?: (name: string, contextRef: string, value: string, qname?: string) => void;
  formData?: Record<string, any>; // Add formData prop to receive data from parent
}

interface EquityCounter {
  [key: string]: number;
}

interface PeriodInfo {
  opening: string;
  start: string;
  end: string;
}

// Recursive function to collect all members from the axis structure
const collectMembers = (member: any, allMembers: any[] = []): any[] => {
  if (member.structure?.children) {
    member.structure.children.forEach((child: any) => {
      collectMembers(child, allMembers);
    });
  }

  // Exclude certain member types
  if (member.name &&
    !['ClassesOfShareCapitalMember', 'EntitysTotalForRelatedPartiesMember'].includes(member.name)) {
    if (!allMembers.find(m => m.name === member.name)) {
      allMembers.push(member);
    }
  }

  return allMembers;
};

// Helper to format date strings (remove dashes for context refs, or return as-is if already formatted)
const formatDateForContext = (dateStr: string): string => {
  // If date is already in YYYYMMDD format, return as-is
  if (/^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  // Otherwise remove dashes
  return dateStr.replace(/-/g, '');
};

// Helper to format date for display (YYYYMMDD -> YYYY-MM-DD)
const formatDateForDisplay = (dateStr: string): string => {
  // If date is in YYYYMMDD format, convert to YYYY-MM-DD
  if (/^\d{8}$/.test(dateStr)) {
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
  }
  // Otherwise return as-is (already formatted)
  return dateStr;
};

// Get period context based on item type and period info
const getContextRef = (
  item: any,
  periodInfo: PeriodInfo,
  memberSuffix: string,
  memberName?: string,
  isOpening: boolean = false
): string => {
  const formattedEnd = formatDateForContext(periodInfo.end);
  const formattedStart = formatDateForContext(periodInfo.start);
  const formattedOpening = formatDateForContext(periodInfo.opening);

  // Check if member is root member (SOCIE: Equity/Funds, PPE: PropertyPlantAndEquipment, IA: IntangibleAssets)
  const isRootMember = memberName && [
    'EquityMember', 
    'FundsAndReservesMember',
    'PropertyPlantAndEquipmentMember',  // Singapore PPE total column
    'IntangibleAssetsMember'  // Singapore Intangible Assets total column
  ].includes(memberName);

  // For Equity items that need opening period
  if (isOpening) {
    if (isRootMember) {
      return `asof_${formattedOpening}_${memberSuffix}`;
    } else {
      return `asof_${formattedOpening}_${memberSuffix}_${memberName}`;
    }
  }

  // For duration period types
  if (item.properties?.period_type === 'duration') {
    if (isRootMember) {
      return `fromto_${formattedStart}_${formattedEnd}_${memberSuffix}`;
    } else {
      return `fromto_${formattedStart}_${formattedEnd}_${memberSuffix}_${memberName}`;
    }
  }

  // For instant period types (default)
  if (isRootMember) {
    return `asof_${formattedEnd}_${memberSuffix}`;
  } else {
    return `asof_${formattedEnd}_${memberSuffix}_${memberName}`;
  }
};

interface LineItemProps {
  item: any;
  members: any[];
  periodInfo: PeriodInfo;
  memberSuffix: string;
  equityCounters: EquityCounter;
  negativeTargets: string[];
  readOnlyFields: Set<string>;
  formData: Record<string, string>;
  onFieldChange: (name: string, contextRef: string, value: string) => void;
  onGetCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  level: number;
  getTestOccurrence?: (itemName: string, itemKey: string) => number;
  itemIndex?: number;
}

// Financial input component with state (SAME AS SOFP!)
const EquityFinancialInput: React.FC<{
  item: any;
  member: any;
  contextRef: string;
  fieldId: string;
  fieldName: string;
  isReadonly: boolean;
  value: string;
  onFieldChange: (name: string, contextRef: string, value: string, qname?: string) => void;
  specialAttributes: Record<string, string>;
}> = ({ item, member, contextRef, fieldId, fieldName, isReadonly: _isReadonly, value, onFieldChange, specialAttributes }) => {
  const [computedStyle, setComputedStyle] = useState<React.CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Format value for display
  const formatValue = useCallback((val: string, isReadonlyField: boolean): string => {
    if (!val || val.trim() === '') {
      return isReadonlyField ? '0.00' : '';
    }

    const numericValue = numeral(val).value();
    if (numericValue === null || isNaN(numericValue)) {
      return isReadonlyField ? '0.00' : '';
    }

    return numeral(numericValue).format('0,0.00');
  }, []);

  // Format initial value
  const initialValue = useMemo(() => {
    if (!value || value.trim() === '') return '';
    return formatValue(value, false);
  }, [value, formatValue]);

  // Apply styling based on readonly status (set by HyperFormula hook)
  useEffect(() => {
    const checkReadonly = () => {
      if (inputRef.current && inputRef.current.readOnly) {
        setComputedStyle({ backgroundColor: 'var(--bg-white-dark)', color: '#495057', fontWeight: 'bold' });
      }
    };

    // Check after HyperFormula has time to initialize
    const timer = setTimeout(checkReadonly, 600);
    return () => clearTimeout(timer);
  }, []);

  const handleFocus = useCallback(() => {
    // For uncontrolled editable inputs, show raw numeric value when focused (remove formatting)
    if (inputRef.current && !inputRef.current.readOnly) {
      const currentValue = inputRef.current.value || '';
      const numericValue = numeral(currentValue).value();
      if (numericValue !== null) {
        inputRef.current.value = numericValue.toString();
      }
    }
  }, []);

  const handleBlur = useCallback(() => {
    // CRITICAL: Clear any pending debounced update and process immediately
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Format the value on blur and update ref immediately
    if (inputRef.current && !inputRef.current.readOnly) {
      // Check XBRL mandatory fields first (set 0.00 if empty)
      handleXBRLFieldBlur(inputRef.current);
      
      const currentValue = inputRef.current.value || '';
      const formatted = formatValue(currentValue, false);

      // Always update DOM to show formatted value
      if (formatted !== currentValue) {
        inputRef.current.value = formatted;
      }

      // Update parent
      onFieldChange(fieldName, contextRef, formatted || currentValue || '', item.qname);
    }
  }, [formatValue, fieldName, contextRef, onFieldChange, item.qname]);

  // Prevent non-numeric characters from being entered (only allow numbers, decimal, minus, comma)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputRef.current?.readOnly) return;
    
    // Allow: Backspace, Delete, Tab, Escape, Enter, Arrow keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88, 90].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Home, End
    if ([35, 36].includes(e.keyCode)) {
      return;
    }
    
    // Allow: numbers (0-9) on main keyboard and numpad
    if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    
    // Allow: decimal point (.) and comma (,)
    if ([190, 188, 110].includes(e.keyCode)) {
      return;
    }
    
    // Allow: minus sign (-) for negative numbers
    if ([189, 109].includes(e.keyCode)) {
      return;
    }
    
    // Prevent all other keys (including alphabets)
    e.preventDefault();
  }, []);

  // Handle input changes - notify parent (which calls HyperFormula)
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Immediate UI-only recompute signal so computed totals feel instant while typing.
    document.dispatchEvent(new CustomEvent('ppeComputedTotalNeedsUpdate'));

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // DON'T format while typing - just save raw value
      // Formatting ONLY happens on blur
      onFieldChange(fieldName, contextRef, newValue || '', item.qname);
    }, 300);
  }, [fieldName, contextRef, onFieldChange, item.qname]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="footnote-display-group">

      <input
        ref={inputRef}
        type="text"
        className="form-control financial-input"
        name={fieldName}
        data-qname={item.qname}
        data-context-ref={contextRef}
        data-equity-member={member.name}
        data-calculation-target={item.name}
        data-no-fill={item.validation?.no_fill || 'N'}
        data-balance={item.properties?.balance}
        data-type={item.properties?.data_type}
        data-period-type={item.properties?.period_type}
        data-field-id={fieldId}
        defaultValue={initialValue}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={computedStyle}
        {...specialAttributes}
      />
      <ValidationIndicator fieldName={fieldName} contextRef={contextRef} />
    </div>
  );
};

const EquityLineItem: React.FC<LineItemProps> = ({
  item,
  members,
  periodInfo,
  memberSuffix,
  equityCounters,
  negativeTargets,
  readOnlyFields,
  formData,
  onFieldChange,
  onGetCalculatedValue,
  level,
  getTestOccurrence,
  itemIndex = 0
}) => {

  // Create a stable unique key for this item based on its index and level
  const itemKey = `${level}_${itemIndex}`;

  // Get occurrence for this item (all non-abstract items, any level) - same as SOFPTable
  const testOccurrence = !item.properties?.abstract && getTestOccurrence ?
    getTestOccurrence(item.name, itemKey) : null;

  // Handle abstract items (section headers)
  if (item.properties?.abstract) {
    return (
      <>
        <tr>
          <td
            className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}
            style={{ paddingLeft: `${(level + 1) * 15}px` }}
            colSpan={members.length + 1}
          >
            {item.standard_label}
          </td>
        </tr>
        {item.structure?.children?.map((child: any, index: number) => (
          <EquityLineItem
            key={`${child.name}_${index}`}
            item={child}
            members={members}
            periodInfo={periodInfo}
            memberSuffix={memberSuffix}
            equityCounters={equityCounters}
            negativeTargets={negativeTargets}
            readOnlyFields={readOnlyFields}
            formData={formData}
            onFieldChange={onFieldChange}
            onGetCalculatedValue={onGetCalculatedValue}
            level={level + 1}
            getTestOccurrence={getTestOccurrence}
            itemIndex={index}
          />
        ))}
      </>
    );
  }

  // Handle items with special occurrence tracking (opening/closing)
  // These items appear twice: once for opening balance, once for closing balance
  const isEquityItem = [
    // SOCIE equity items
    'Equity', 'EquityBalanceRestated', 'FundBalance',
    // PPE items that appear twice (opening/closing)
    // Note: Singapore taxonomy uses "On" not "Of" for AccumulatedDepreciation
    'GrossCarryingAmountOfPropertyPlantAndEquipment',
    'AccumulatedDepreciationAmortisationAndImpairmentOnPropertyPlantAndEquipment',
    'PropertyPlantAndEquipment',
    // Intangible Assets items that appear twice (opening/closing)
    'GrossCarryingAmountOfIntangibleAssets',
    'AmortisationAndImpairmentOnIntangibleAssetsOtherThanGoodwillGross',
    'IntangibleAssetsOtherThanGoodwill'
  ].includes(item.name);

  const isPPETable = members.some(m => m.name === 'PropertyPlantAndEquipmentMember');
  const isIATable = members.some(m => m.name === 'IntangibleAssetsMember');

  const shouldRenderComputedCostValuationTotalRow =
    (isPPETable && item.name === 'PropertyPlantAndEquipmentAtValuation') ||
    (isIATable && item.name === 'IntangibleAssetsOtherThanGoodwillAtValuation');
  const rootMemberName = isPPETable ? 'PropertyPlantAndEquipmentMember' : 'IntangibleAssetsMember';

  const costQname = isPPETable
    ? 'sg-as:PropertyPlantAndEquipmentAtCost'
    : 'sg-as:IntangibleAssetsOtherThanGoodwillAtCost';
  const valuationQname = isPPETable
    ? 'sg-as:PropertyPlantAndEquipmentAtValuation'
    : 'sg-as:IntangibleAssetsOtherThanGoodwillAtValuation';
  const computedFieldName = isPPETable
    ? 'PPE-ComputedAtCostAndValuationTotal'
    : 'IA-ComputedAtCostAndValuationTotal';

  const parseNumeric = (raw: string | undefined | null): number => {
    if (!raw || raw.trim() === '') return 0;
    const n = parseFloat(raw.replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const getContextRefForMember = (member: any): string => {
    if (isEquityItem && testOccurrence) {
      const isOpening = testOccurrence === 1;
      const isRootMember = [
        'PropertyPlantAndEquipmentMember',
        'IntangibleAssetsMember'
      ].includes(member.name);

      if (isOpening) {
        return isRootMember
          ? `asof_${periodInfo.opening}_${memberSuffix}`
          : `asof_${periodInfo.opening}_${memberSuffix}_${member.name}`;
      }

      return isRootMember
        ? `asof_${periodInfo.end}_${memberSuffix}`
        : `asof_${periodInfo.end}_${memberSuffix}_${member.name}`;
    }

    return getContextRef(item, periodInfo, memberSuffix, member.name, false);
  };

  const getComputedTotal = (contextRef: string): string => {
    const costKey = `${costQname}_${contextRef}`;
    const valuationKey = `${valuationQname}_${contextRef}`;
    const costRef = formData[costKey];
    const valuationRef = formData[valuationKey];

    const costFromRef = parseNumeric(costRef);
    const valuationFromRef = parseNumeric(valuationRef);

    // Fallback to DOM when ref has not synced yet
    const costFromDom = parseNumeric(
      (document.querySelector(`input[data-qname="${costQname}"][data-context-ref="${contextRef}"]`) as HTMLInputElement | null)?.value
    );
    const valuationFromDom = parseNumeric(
      (document.querySelector(`input[data-qname="${valuationQname}"][data-context-ref="${contextRef}"]`) as HTMLInputElement | null)?.value
    );

    const costValue = costRef !== undefined && String(costRef).trim() !== '' ? costFromRef : costFromDom;
    const valuationValue = valuationRef !== undefined && String(valuationRef).trim() !== '' ? valuationFromRef : valuationFromDom;
    return numeral(costValue + valuationValue).format('0,0.00');
  };

  const getComputedGrandTotalForRoot = (): string => {
    let total = 0;
    members.forEach((member) => {
      if (member.name === rootMemberName) return;
      const contextRef = getContextRefForMember(member);
      total += parseNumeric(getComputedTotal(contextRef));
    });
    return numeral(total).format('0,0.00');
  };

  return (
    <>
      <tr>
        <td
          className={`
          ${level === 0 ? 'table-title-0 c-black' : ''}
          ${level === 1 ? 'table-title-1 c-theme-600' : ''}
          ${level === 2 ? 'table-title-2 c-theme-500' : ''}
          ${level === 3 ? 'table-title-3 c-dark' : ''}
          ${level >= 4 ? 'table-title-4 c-light' : ''}
          ${negativeTargets.includes(item.name) ? 'negative-indicator' : ''}
        `}
          style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
          title={negativeTargets.includes(item.name) ? 'Subtracted from total' : ''}
        >
          {item.standard_label}
          {/* Show period indicator for items that appear twice (opening/closing) */}
          {isEquityItem && testOccurrence && (
            <span style={{ marginLeft: '8px', color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
              {testOccurrence === 1 ? '(Beginning of period)' : '(Closing of period)'}
            </span>
          )}
        </td>

        {members.map((member, memberIndex) => {
          const contextRef = getContextRefForMember(member);

          // Field name prefix for HyperFormula (detect table type from member name)
          // PPE tables have PropertyPlantAndEquipmentMember, IA tables have IntangibleAssetsMember, SOCIE tables have EquityMember/FundsAndReservesMember
          const fieldPrefix = isPPETable ? 'PPE' : (isIATable ? 'IA' : 'Equity');
          const fieldName = `${fieldPrefix}-${item.name}`;
          const fieldId = `${contextRef}_${item.qname}`;

          // PRIMARY KEY for data lookup: qname + contextRef
          const dataKey = `${item.qname}_${contextRef}`;

          // DON'T use readOnlyFields Set for SOCIE - it has conflicts when:
          // Previous Year Closing and Current Year Opening share the same contextRef/date
          // Instead, the hook sets readOnly directly on DOM elements, and React will read it from there
          // For now, assume all fields are editable (EquityFinancialInput will check DOM after render)
          const isReadonly = false;

          // Get field value using qname + contextRef
          const fieldValue = formData[dataKey] || '';

          // Special attributes for equity items
          const specialAttributes: Record<string, string> = {};
          if (item.name === 'EquityBalanceRestated' || item.name === 'FundBalanceRestated') {
            specialAttributes['data-equity-source'] = 'opening';
          }
          if (item.name === 'ChangesInEquity' || item.name === 'ChangesInFunds') {
            specialAttributes['data-equity-source'] = 'changes';
          }
          if ((item.name === 'Equity' || item.name === 'FundBalance') && testOccurrence === 2) {
            specialAttributes['data-equity-target'] = 'closing';
          }

          // PPE-specific data attributes for HyperFormula relationship mapping
          // GrossCarryingAmount (Cost): opening/closing
          if (item.name === 'GrossCarryingAmountOfPropertyPlantAndEquipment') {
            if (testOccurrence === 1) {
              specialAttributes['data-cost-opening'] = 'true';
            } else if (testOccurrence === 2) {
              specialAttributes['data-cost-closing'] = 'true';
            }
          }
          // AccumulatedDepreciation: opening/closing
          if (item.name === 'AccumulatedDepreciationAmortisationAndImpairmentOnPropertyPlantAndEquipment') {
            if (testOccurrence === 1) {
              specialAttributes['data-depr-opening'] = 'true';
            } else if (testOccurrence === 2) {
              specialAttributes['data-depr-closing'] = 'true';
            }
          }
          // PropertyPlantAndEquipment (NBV): opening/closing
          if (item.name === 'PropertyPlantAndEquipment') {
            if (testOccurrence === 1) {
              specialAttributes['data-nbv-opening'] = 'true';
            } else if (testOccurrence === 2) {
              specialAttributes['data-nbv-closing'] = 'true';
            }
          }

          // Intangible Assets-specific data attributes for HyperFormula relationship mapping
          // GrossCarryingAmount (Cost): opening/closing
          if (item.name === 'GrossCarryingAmountOfIntangibleAssets') {
            if (testOccurrence === 1) {
              specialAttributes['data-cost-opening'] = 'true';
            } else if (testOccurrence === 2) {
              specialAttributes['data-cost-closing'] = 'true';
            }
          }
          // AmortisationAndImpairment (Depr): opening/closing
          if (item.name === 'AmortisationAndImpairmentOnIntangibleAssetsOtherThanGoodwillGross') {
            if (testOccurrence === 1) {
              specialAttributes['data-depr-opening'] = 'true';
            } else if (testOccurrence === 2) {
              specialAttributes['data-depr-closing'] = 'true';
            }
          }
          // IntangibleAssetsOtherThanGoodwill (NBV): opening/closing
          if (item.name === 'IntangibleAssetsOtherThanGoodwill') {
            if (testOccurrence === 1) {
              specialAttributes['data-nbv-opening'] = 'true';
            } else if (testOccurrence === 2) {
              specialAttributes['data-nbv-closing'] = 'true';
            }
          }

          return (
            <td key={memberIndex} className="table-input">
              <div className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}>
                {item.properties?.data_type === 'monetary' && (
                  <EquityFinancialInput
                    item={item}
                    member={member}
                    contextRef={contextRef}
                    fieldId={fieldId}
                    fieldName={fieldName}
                    isReadonly={isReadonly}
                    value={fieldValue}
                    onFieldChange={onFieldChange}
                    specialAttributes={specialAttributes}
                  />
                )}
              </div>
            </td>
          );
        })}
      </tr>
      {shouldRenderComputedCostValuationTotalRow && (
        <tr>
          <td
            className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}
            style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
          >
            At cost + At valuation
          </td>
          {members.map((member, memberIndex) => {
            const contextRef = getContextRefForMember(member);
            const displayValue = member.name === rootMemberName
              ? getComputedGrandTotalForRoot()
              : getComputedTotal(contextRef);

            return (
              <td key={`computed_cost_val_total_${memberIndex}`} className="table-input">
                <div className={`
                  ${level === 0 ? 'table-title-0 c-black' : ''}
                  ${level === 1 ? 'table-title-1 c-theme-600' : ''}
                  ${level === 2 ? 'table-title-2 c-theme-500' : ''}
                  ${level === 3 ? 'table-title-3 c-dark' : ''}
                  ${level >= 4 ? 'table-title-4 c-light' : ''}
                `}>
                  <div className="footnote-display-group">
                    <input
                      type="text"
                      className="form-control financial-input"
                      name={computedFieldName}
                      data-context-ref={contextRef}
                      data-equity-member={member.name}
                      value={displayValue}
                      readOnly={true}
                      style={{ backgroundColor: 'var(--bg-white-dark)', color: '#495057', fontWeight: 'bold' }}
                    />
                    <ValidationIndicator fieldName={computedFieldName} contextRef={contextRef} />
                  </div>
                </div>
              </td>
            );
          })}
        </tr>
      )}
      {item.structure?.children?.map((child: any, index: number) => (
        <EquityLineItem
          key={`${child.name}_${index}`}
          item={child}
          members={members}
          periodInfo={periodInfo}
          memberSuffix={memberSuffix}
          equityCounters={equityCounters}
          negativeTargets={negativeTargets}
          readOnlyFields={readOnlyFields}
          formData={formData}
          onFieldChange={onFieldChange}
          onGetCalculatedValue={onGetCalculatedValue}
          level={level + 1}
          getTestOccurrence={getTestOccurrence}
          itemIndex={index}
        />
      ))}
    </>
  );
};

const PPETableSGComponent: React.FC<PPETableSGProps> = ({
  section,
  companyInfo,
  negativeTargets = [],
  globalHyperFormula,
  onFieldChange: globalFieldChange,
  formData: parentFormData = {}
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  // Use parentFormData directly (single source of truth from EditFilings ref)
  const formData = parentFormData;

  // PPE HyperFormula for Singapore Property Plant and Equipment table
  const ppeHyperFormula = useHyperFormulaPPESG();

  // Use PPE HyperFormula (Singapore only has PPE tables, not SOCIE)
  const hyperFormula = {
    isReady: ppeHyperFormula.isReady,
    readOnlyFields: ppeHyperFormula.readOnlyFields,
    handleFieldChange: ppeHyperFormula.handleFieldChange,
    getCalculatedValue: ppeHyperFormula.getCalculatedValue
  };

  // Local occurrence counter (same logic as SOFPTable)
  const occurrenceCounterRef = useRef<Map<string, number>>(new Map());

  // Get occurrence count (stable - doesn't change on re-renders, same as SOFPTable)
  const getTestOccurrence = useCallback((itemName: string, itemKey: string): number => {
    // Use a unique key that includes item position to ensure stability
    const uniqueKey = `${itemName}_${itemKey}`;

    if (!occurrenceCounterRef.current.has(uniqueKey)) {
      // Only increment the counter for this item name when we see a new unique key
      const itemCount = Array.from(occurrenceCounterRef.current.keys())
        .filter(key => key.startsWith(`${itemName}_`)).length + 1;

      occurrenceCounterRef.current.set(uniqueKey, itemCount);
    }

    return occurrenceCounterRef.current.get(uniqueKey)!;
  }, []);

  // Extract members and line items from section structure
  const { members, lineItems } = useMemo(() => {
    const axis = section.structure?.children?.[0];
    const items = section.structure?.children?.[1];

    let allMembers: any[] = [];

    // Collect members from axis structure (second child's first child)
    if (axis?.structure?.children?.[1]?.structure?.children?.[0]) {
      const equityRoot = axis.structure.children[1].structure.children[0];
      allMembers = collectMembers(equityRoot);
    }

    // Limit to first 100 members (as per original macro)
    allMembers = allMembers.slice(0, 100);

    // Get line items
    const lineItemsList = items?.structure?.children || [];

    return { members: allMembers, lineItems: lineItemsList };
  }, [section]);

  // Get period information from companyInfo or form structure metadata
  const getPeriodInfo = useCallback((isPrevious: boolean = false): PeriodInfo => {
    // Try to get from companyInfo first (check both naming conventions)
    const currentPeriod = companyInfo.currentPeriod || companyInfo.reporting_period;
    const previousPeriod = companyInfo.previousPeriod || companyInfo.previous_period;

    if (currentPeriod && previousPeriod) {
      const period = isPrevious ? previousPeriod : currentPeriod;
      return {
        opening: formatDateForContext(period.opening),
        start: formatDateForContext(period.start),
        end: formatDateForContext(period.end)
      };
    }

    // Fallback: return placeholder dates (should not happen if companyInfo is properly extended)
    return {
      opening: '20240430',
      start: '20240501',
      end: '20250430'
    };
  }, [companyInfo]);

  // Recompute UI-only computed totals in PPE/IA tables:
  // - each non-root member cell = At cost + At valuation
  // - right-most root member cell = sum of all non-root member computed totals
  const updateComputedCostValuationRows = useCallback(() => {
    const parseNumeric = (raw: string | undefined | null): number => {
      if (!raw || raw.trim() === '') return 0;
      const n = parseFloat(raw.replace(/[^\d.-]/g, ''));
      return Number.isFinite(n) ? n : 0;
    };

    const recomputeForTable = (
      table: HTMLTableElement,
      computedFieldName: string,
      costQname: string,
      valuationQname: string,
      rootMemberName: string
    ) => {
      const computedInputs = Array.from(
        table.querySelectorAll(`input[name="${computedFieldName}"]`)
      ) as HTMLInputElement[];

      let grandTotal = 0;
      computedInputs.forEach((computedInput) => {
        const contextRef = computedInput.getAttribute('data-context-ref');
        const memberName = computedInput.getAttribute('data-equity-member') || '';
        if (!contextRef || memberName === rootMemberName) return;

        const costInput = table.querySelector(
          `input[data-qname="${costQname}"][data-context-ref="${contextRef}"]`
        ) as HTMLInputElement | null;
        const valuationInput = table.querySelector(
          `input[data-qname="${valuationQname}"][data-context-ref="${contextRef}"]`
        ) as HTMLInputElement | null;

        const memberTotal = parseNumeric(costInput?.value) + parseNumeric(valuationInput?.value);
        grandTotal += memberTotal;
        computedInput.value = numeral(memberTotal).format('0,0.00');
      });

      const rootInput = computedInputs.find(
        (el) => (el.getAttribute('data-equity-member') || '') === rootMemberName
      );
      if (rootInput) {
        rootInput.value = numeral(grandTotal).format('0,0.00');
      }
    };

    const container = rootRef.current;
    if (!container) return;
    const tables = Array.from(container.querySelectorAll('table.socie-table')) as HTMLTableElement[];
    tables.forEach((table) => {
      recomputeForTable(
        table,
        'PPE-ComputedAtCostAndValuationTotal',
        'sg-as:PropertyPlantAndEquipmentAtCost',
        'sg-as:PropertyPlantAndEquipmentAtValuation',
        'PropertyPlantAndEquipmentMember'
      );
      recomputeForTable(
        table,
        'IA-ComputedAtCostAndValuationTotal',
        'sg-as:IntangibleAssetsOtherThanGoodwillAtCost',
        'sg-as:IntangibleAssetsOtherThanGoodwillAtValuation',
        'IntangibleAssetsMember'
      );
    });
  }, []);

  const handleFieldChange = useCallback((name: string, contextRef: string, value: string, qname?: string) => {
    // Use qname + contextRef as primary key if provided
    const dataKey = qname ? `${qname}_${contextRef}` : `${name}_${contextRef}`;

    // Update form data for display - REMOVED (using parent ref directly)
    // setFormData(prev => ({ ...prev, [dataKey]: value }));

    // ALWAYS call local HyperFormula first (for calculations)
    if (hyperFormula.isReady && hyperFormula.handleFieldChange) {
      hyperFormula.handleFieldChange(name, contextRef, value);
    }

    // THEN call global field change handler (for parent state management / saving)
    if (globalFieldChange) {
      globalFieldChange(name, contextRef, value, qname);
    }
    // Keep UI-only computed totals in sync immediately
    setTimeout(() => updateComputedCostValuationRows(), 0);
  }, [globalFieldChange, hyperFormula.isReady, hyperFormula.handleFieldChange, updateComputedCostValuationRows]);

  const getCalculatedValue = useCallback((fieldName: string, contextRef: string): number | null => {
    if (!hyperFormula.isReady || !hyperFormula.getCalculatedValue) return null;
    return hyperFormula.getCalculatedValue(fieldName, contextRef);
  }, [hyperFormula.isReady, hyperFormula.getCalculatedValue]);

  // Render a single table for a specific period and member type
  const renderSingleTable = useCallback((
    periodInfo: PeriodInfo,
    memberSuffix: string,
    isPreviousPeriod: boolean = false
  ) => {
    // Reset occurrence counter for each new table (each table should start fresh)
    // Include table identifier in counter key to keep tables separate
    const tableKey = `${memberSuffix}_${periodInfo.start}_${periodInfo.end}`;

    // Create a table-specific getTestOccurrence that includes table key
    const getTableTestOccurrence = (itemName: string, itemKey: string): number => {
      const uniqueKey = `${tableKey}_${itemName}_${itemKey}`;

      if (!occurrenceCounterRef.current.has(uniqueKey)) {
        // Count occurrences of this item name within this table only
        const itemCount = Array.from(occurrenceCounterRef.current.keys())
          .filter(key => key.startsWith(`${tableKey}_${itemName}_`)).length + 1;

        occurrenceCounterRef.current.set(uniqueKey, itemCount);
      }

      return occurrenceCounterRef.current.get(uniqueKey)!;
    };

    // Note: equityCounters parameter is kept for compatibility but not used anymore
    // We use testOccurrence instead (same as SOFPTable)
    const equityCounters: EquityCounter = {};

    return (
      <div key={`${memberSuffix}_${periodInfo.start}_${periodInfo.end}`} className="socie-table-section">
        <h3 className="section-header">
          <span>
            Period: {formatDateForDisplay(periodInfo.start)} to {formatDateForDisplay(periodInfo.end)}, {memberSuffix}
          </span>
          {(memberSuffix === 'SeparateMember' || memberSuffix === 'ConsolidatedMember') && (
            <button
              type="button"
              className="table-toggle-button"
              onClick={(e) => {
                e.preventDefault();
                const sectionEl = (e.currentTarget as HTMLButtonElement).closest('.socie-table-section') as HTMLElement | null;
                if (sectionEl) toggleColumnVisibilityForSection(sectionEl);
              }}
            >
              Show All Columns
            </button>
          )}
        </h3>
        <div className="card-table" style={{ overflow: 'auto' }}>
          <table
            className="table-element socie-table"
            data-datatable-table="true"
            data-period-start={formatDateForContext(periodInfo.start)}
            data-period-end={formatDateForContext(periodInfo.end)}
            data-member-suffix={memberSuffix}
          >
            <thead>
              <tr>
                <th>
                  <span className="sort">
                    <span className="sort-label"></span>
                  </span>
                </th>
                {members.map((member, index) => (
                  <th key={index}>
                    <span className="sort">
                      <span className="sort-label">{member.standard_label}</span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item: any, index: number) => (
                <EquityLineItem
                  key={`${item.name}_${item.qname}_${index}`}
                  item={item}
                  members={members}
                  periodInfo={periodInfo}
                  memberSuffix={memberSuffix}
                  equityCounters={equityCounters}
                  negativeTargets={negativeTargets}
                  readOnlyFields={hyperFormula.readOnlyFields}
                  formData={formData}
                  onFieldChange={handleFieldChange}
                  onGetCalculatedValue={getCalculatedValue}
                  level={0}
                  getTestOccurrence={getTableTestOccurrence}
                  itemIndex={index}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [members, lineItems, negativeTargets, hyperFormula.readOnlyFields, formData, handleFieldChange, getCalculatedValue, occurrenceCounterRef]);

  // Default behavior: hide empty member columns after PPE calculations are ready
  useEffect(() => {
    const run = () => {
      const container = rootRef.current;
      if (!container) return;
      const targetTables = container.querySelectorAll(
        'table.socie-table[data-member-suffix="SeparateMember"], table.socie-table[data-member-suffix="ConsolidatedMember"]'
      ) as NodeListOf<HTMLTableElement>;
      targetTables.forEach((t) => applyHideEmptyColumns(t));
    };

    // 🔒 FIX: Increased delay to ensure HyperFormula has fully updated DOM
    // On fast machines, the event might fire before DOM reflects calculated values
    const onComplete = () => setTimeout(() => {
      run();
      updateComputedCostValuationRows();
    }, 300);  // Increased from 50ms to 300ms
    // PPE/IA hook emits ppeCalculationsComplete. Keep socie event as fallback compatibility.
    document.addEventListener('ppeCalculationsComplete', onComplete);
    document.addEventListener('socieCalculationsComplete', onComplete);
    const onDataLoaded = () => setTimeout(() => updateComputedCostValuationRows(), 80);
    document.addEventListener('formDataLoaded', onDataLoaded);
    document.addEventListener('specialFieldsCalculated', onDataLoaded);
    const onImmediateRecompute = () => updateComputedCostValuationRows();
    document.addEventListener('ppeComputedTotalNeedsUpdate', onImmediateRecompute);
    return () => {
      document.removeEventListener('ppeCalculationsComplete', onComplete);
      document.removeEventListener('socieCalculationsComplete', onComplete);
      document.removeEventListener('formDataLoaded', onDataLoaded);
      document.removeEventListener('specialFieldsCalculated', onDataLoaded);
      document.removeEventListener('ppeComputedTotalNeedsUpdate', onImmediateRecompute);
    };
  }, [updateComputedCostValuationRows]);

  // Determine which tables to render based on company info
  const tablesToRender = useMemo(() => {
    const currentPeriod = getPeriodInfo(false);
    const previousPeriod = getPeriodInfo(true);
    const tables: JSX.Element[] = [];

    if (companyInfo.first_year === 'N') {
      if (companyInfo.consolidated === 'Y') {
        tables.push(renderSingleTable(currentPeriod, 'ConsolidatedMember', false));
        tables.push(renderSingleTable(previousPeriod, 'ConsolidatedMember', true));
        tables.push(renderSingleTable(currentPeriod, 'SeparateMember', false));
        tables.push(renderSingleTable(previousPeriod, 'SeparateMember', true));
      } else {
        tables.push(renderSingleTable(currentPeriod, 'SeparateMember', false));
        tables.push(renderSingleTable(previousPeriod, 'SeparateMember', true));
      }
    } else {
      if (companyInfo.consolidated === 'Y') {
        tables.push(renderSingleTable(currentPeriod, 'ConsolidatedMember', false));
        tables.push(renderSingleTable(currentPeriod, 'SeparateMember', false));
      } else {
        tables.push(renderSingleTable(currentPeriod, 'SeparateMember', false));
      }
    }

    return tables;
  }, [companyInfo, getPeriodInfo, renderSingleTable]);

  return (
    <div ref={rootRef} className="card">

      <div className="card-body" style={{ padding: '0rem' }}>
        {tablesToRender}
      </div>
    </div>
  );
};

// Memoize to prevent unnecessary re-renders when parent state changes
export const PPETableSG = React.memo(PPETableSGComponent);

